import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, CreditCard, Users, GitBranch, TrendingUp, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../utils/api';

export const CompanyAdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [dashStats, setDashStats] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.companyId) return;
    Promise.all([
      apiClient.getDashboardStats(),
      apiClient.listUsers(user.companyId, 100, 0),
      apiClient.listBranches(user.companyId, 100, 0),
    ]).then(([ds, u, b]) => {
      setDashStats(ds.data.data);
      const ud = u.data.data; setUsers(ud?.users ?? ud?.rows ?? (Array.isArray(ud) ? ud : []));
      const bd = b.data.data; setBranches(bd?.branches ?? bd?.rows ?? (Array.isArray(bd) ? bd : []));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  const s = dashStats?.shipments;
  const pendingPayments = dashStats?.payments?.pending?.count || 0;
  const activeBranches = branches.filter(b => b.status === 'ACTIVE').length;

  const stats = [
    { label: 'Total Delivery Orders', value: s?.total ?? 0, sub: `${s?.inTransit ?? 0} in transit`, color: '#3b82f6', link: '/company-admin/shipments', icon: <Package size={20} /> },
    { label: 'Delivery Rate', value: `${s?.deliveryRate ?? 0}%`, sub: 'of all shipments', color: '#22c55e', link: '/company-admin/shipments', icon: <TrendingUp size={20} /> },
    { label: 'Pending Payments', value: pendingPayments, sub: 'awaiting collection', color: '#ef4444', link: '/company-admin/payments', icon: <CreditCard size={20} /> },
    { label: 'Staff Members', value: users.length, sub: 'in your company', color: '#6366f1', link: '/company-admin/users', icon: <Users size={20} /> },
    { label: 'Active Branches', value: activeBranches, sub: `of ${branches.length} total`, color: '#22c55e', link: '/company-admin/branches', icon: <GitBranch size={20} /> },
  ];

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Delivery Dashboard</h1>
        <Link to="/company-admin/shipments/create" className="btn btn-primary"><Package size={16} /> New Delivery Order</Link>
      </div>

      {loading ? <div className="loading-spinner">Loading...</div> : (
        <>
          <div className="stats-grid">
            {stats.map(stat => (
              <Link to={stat.link} key={stat.label} style={{ textDecoration: 'none' }}>
                <div className="stat-card" style={{ borderLeft: `4px solid ${stat.color}`, cursor: 'pointer' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                      <div className="stat-label">{stat.label}</div>
                      <div className="stat-value" style={{ color: stat.color, fontSize: typeof stat.value === 'string' ? 20 : 28 }}>{stat.value}</div>
                      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{stat.sub}</div>
                    </div>
                    <div style={{ color: stat.color, opacity: 0.6 }}>{stat.icon}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {(s?.pending ?? 0) > 0 && (
            <div className="card" style={{ background: '#fef9c3', border: '1px solid #fde047', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#854d0e' }}>
                <AlertCircle size={18} />
                <span><strong>{s.pending}</strong> shipments awaiting confirmation.</span>
                <Link to="/company-admin/shipments?status=PENDING" className="btn btn-ghost" style={{ marginLeft: 'auto', fontSize: 12 }}>Review</Link>
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div className="card">
              <div className="page-header" style={{ marginBottom: 16 }}>
                <h2 style={{ fontSize: 15, fontWeight: 600 }}>Recent Shipments</h2>
                <Link to="/company-admin/shipments" className="btn btn-ghost" style={{ fontSize: 12 }}>View all</Link>
              </div>
              <div className="table-wrapper">
                <table>
                  <thead><tr><th>Tracking #</th><th>Receiver</th><th>Status</th></tr></thead>
                  <tbody>
                    {(dashStats?.recentShipments ?? []).map((s: any) => (
                      <tr key={s.id}>
                        <td><code style={{ fontSize: 11 }}>{s.trackingNumber}</code></td>
                        <td>{s.receiverName}</td>
                        <td><span className={`badge badge-${s.status?.toLowerCase().replace(/_/g, '-')}`}>{s.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card">
              <div className="page-header" style={{ marginBottom: 16 }}>
                <h2 style={{ fontSize: 15, fontWeight: 600 }}>Branches</h2>
                <Link to="/company-admin/branches" className="btn btn-ghost" style={{ fontSize: 12 }}>Manage</Link>
              </div>
              <div className="table-wrapper">
                <table>
                  <thead><tr><th>Name</th><th>City</th><th>Status</th></tr></thead>
                  <tbody>
                    {branches.slice(0, 6).map((b: any) => (
                      <tr key={b.id}>
                        <td style={{ fontWeight: 500 }}>{b.name}</td>
                        <td>{b.city || '—'}</td>
                        <td><span className={`badge badge-${b.status?.toLowerCase()}`}>{b.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: 20 }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Quick Actions</h2>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {[
                { label: 'New Delivery Order', to: '/company-admin/shipments/create', icon: <Package size={15} /> },
                { label: 'My Orders', to: '/company-admin/shipments', icon: <Package size={15} /> },
                { label: 'Manage Users', to: '/company-admin/users', icon: <Users size={15} /> },
                { label: 'Manage Branches', to: '/company-admin/branches', icon: <GitBranch size={15} /> },
                { label: 'Payments', to: '/company-admin/payments', icon: <CreditCard size={15} /> },
                { label: 'API Keys', to: '/company-admin/api-keys', icon: <TrendingUp size={15} /> },
              ].map(a => (
                <Link key={a.label} to={a.to} className="btn btn-ghost">{a.icon} {a.label}</Link>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
};
